import {
  ApiError,
  completeContent,
  getFile,
  startContent,
  versionUrl,
  type Entity,
} from './api.js';
import { urlFor } from './thumbs.js';

/**
 * **Le cycle d'enregistrement d'une note** — le seul endroit du client où il y a
 * un conflit.
 *
 * Renommer, retaguer, déplacer ne conflictent jamais : chaque op de
 * `PATCH /files/{fid}` est atomique sur son propre attribut, et deux appareils
 * convergent sans arbitrage. Le **corps** d'une note, lui, est écrit en entier
 * par deux appareils qui ne se voient pas, et le serveur le traite par un verrou
 * adossé à `ContentRev`.
 *
 * Le protocole a trois temps, et le second est **hors de l'API** :
 *
 * ① `POST …/content { baseRev }` prend le verrou et rend une URL présignée qui
 *    **meurt avec lui**, à la seconde près.
 * ② le `PUT` va directement à S3. Il rend un `x-amz-version-id` — et c'est le
 *    seul moment où ce numéro existe, personne ne peut le redemander ensuite.
 * ③ `POST …/content/complete { token, versionId }` fait **constater** les octets
 *    à cette version-là, écrit la révision et retire le verrou.
 *
 * **Le `versionId` est ce qui rend un `PUT` tardif inoffensif** : le commit est
 * pinné dessus, et toute lecture présigne `GET ?versionId=…`. Un client réveillé
 * après avoir perdu son verrou ne crée qu'une version S3 morte, que personne ne
 * lira. Sans lui, la fenêtre de présignature serait la seule protection — et une
 * URL présignée ne se révoque pas.
 */

/**
 * Ce que l'appelant doit distinguer. Quatre codes serveur, **quatre conduites** :
 * ce n'est pas de la taxonomie, c'est ce qui décide de ce que l'écran propose.
 */
export type SaveOutcome =
  /**
   * Enregistré. `rev` est la révision neuve, elle devient la base suivante.
   *
   * `excerpt` est **ce que la tuile affichera** : le serveur le recalcule au
   * `complete` et le rend dans la même réponse, ce qui évite de relire l'entité
   * pour rafraîchir deux lignes de texte. `null` dit « cette note est vide » —
   * l'attribut doit alors disparaître, pas rester sur son ancienne valeur — et
   * **absent** dit « ce serveur ne sait pas répondre à cette question », ce qui
   * n'autorise rien du tout.
   */
  | {
      readonly ok: true;
      readonly rev: number;
      readonly size: number;
      readonly updatedAt: string;
      readonly excerpt?: string | null;
    }
  /**
   * Quelqu'un a enregistré depuis qu'on a ouvert. Le texte distant est joint :
   * l'écran peut proposer de comparer, jamais d'écraser en silence.
   */
  | { readonly ok: false; readonly reason: 'stale'; readonly currentRev: number; readonly remote: string }
  /** Quelqu'un édite **en ce moment**. Réessayer après `expiresAt` marchera. */
  | { readonly ok: false; readonly reason: 'locked'; readonly expiresAt: string | null }
  /**
   * Le verrou a été volé pendant l'écriture — session expirée, autre appareil.
   * **Ne pas rejouer le `complete`** : il faut repartir du `POST …/content`.
   */
  | { readonly ok: false; readonly reason: 'lockLost'; readonly currentRev: number }
  /** Les octets n'ont pas été constatés : le `PUT` n'a pas abouti où on croyait. */
  | { readonly ok: false; readonly reason: 'missingBytes' };

/** La révision de base d'une entité. Une note jamais écrite est à **0**. */
export const revOf = (entity: Entity): number => entity.ContentRev ?? 0;

/**
 * **L'entité telle qu'un enregistrement réussi la laisse** *(N5, 2026-08-18)*.
 *
 * Elle est recomposée, jamais relue : le serveur vient de rendre tout ce qu'il a
 * changé, et un `GET /files/{fid}` de plus après chaque `Ctrl+S` serait un
 * aller-retour pour des valeurs qu'on tient déjà.
 *
 * **Elle vit ici, et pas en ligne dans l'écran, parce que c'est là qu'on peut la
 * mesurer** — le geste complet traverse l'API, donc aucun banc sans compte ne le
 * joue. Le défaut qu'elle répare est exactement celui d'une recomposition
 * partielle : elle ne portait ni l'extrait ni la date, si bien que la tuile de la
 * grille montrait l'ancien texte et l'ancienne heure jusqu'au prochain
 * `GET /changes`. Ce qui rend ce défaut discret, c'est qu'**il ne se voit pas
 * depuis l'éditeur** : l'écran qu'on regarde est juste, c'est celui de derrière
 * qui ment.
 *
 * La règle à tenir quand un champ s'ajoutera : *tout ce que le serveur change au
 * `complete` se retrouve ici*, sans quoi le miroir diverge en silence.
 */
export function afterSave(entity: Entity, outcome: Saved): Entity {
  return {
    ...entity,
    ContentRev: outcome.rev,
    UpdatedAt: outcome.updatedAt,
    // **Trois états, trois conduites.** `null` veut dire « la note est vide » et
    // l'attribut cesse d'exister — le laisser afficherait, sous une note
    // effacée, la phrase qu'elle ne contient plus. **Absent** veut dire « ce
    // serveur ne rend pas ce champ », et on garde alors ce qu'on avait : les
    // confondre ferait **effacer l'extrait à chaque enregistrement** pendant la
    // fenêtre où un client neuf parle à l'ancien serveur — une régression pire
    // que le défaut qu'on répare.
    Excerpt: outcome.excerpt === undefined ? entity.Excerpt : (outcome.excerpt ?? undefined),
    Variants: {
      ...(entity.Variants ?? {}),
      content: {
        ext: 'md',
        mime: 'text/markdown',
        ...(entity.Variants?.['content'] ?? {}),
        size: outcome.size,
      },
    },
  };
}

/** L'issue heureuse, nommée : `afterSave` n'accepte qu'elle. */
export type Saved = Extract<SaveOutcome, { readonly ok: true }>;

/**
 * Lit le corps d'une note. **`Variants.content.size` est le drapeau**, jamais
 * `State` : une note créée et jamais enregistrée n'a aucun octet, et demander son
 * URL rendrait `409 bytes_not_ready`. Elle vaut alors la chaîne vide, qui est la
 * vérité — pas une erreur.
 */
export async function readNote(entity: Entity): Promise<string> {
  if (entity.Variants?.['content']?.size === undefined) return '';
  const url = await urlFor(entity.Fid ?? '', 'content');
  // Les octets viennent **de S3**, jamais à travers Lambda — ici comme à l'envoi.
  const res = await fetch(url);
  if (!res.ok) throw new Error(`lecture refusée (${res.status})`);
  return res.text();
}

/** Le corps **à une révision donnée** : la base d'une comparaison, et le restore. */
export async function readRevision(fid: string, rev: number): Promise<string> {
  const signed = await versionUrl(fid, rev);
  const res = await fetch(signed.url);
  if (!res.ok) throw new Error(`lecture refusée (${res.status})`);
  return res.text();
}

/**
 * Enregistre un corps de note, et rend **ce qu'il faut en conclure**.
 *
 * Aucune reprise automatique n'est tentée. Voler un verrou parce qu'on a été
 * refusé, ou réessayer sur `stale_rev` en écrasant, transformerait un conflit
 * visible en perte de données silencieuse — précisément ce que D20 et le verrou
 * existent pour empêcher.
 */
export async function saveNote(
  entity: Entity,
  markdown: string,
  baseRev = revOf(entity),
): Promise<SaveOutcome> {
  const fid = entity.Fid ?? '';

  let lease;
  try {
    lease = await startContent(fid, baseRev);
  } catch (err) {
    if (!(err instanceof ApiError)) throw err;
    if (err.code === 'stale_rev') {
      // On joint le texte distant maintenant : l'écran ne peut pas décider quoi
      // proposer sans lui, et le redemander plus tard doublerait l'aller-retour.
      const fresh = await getFile(fid);
      return {
        ok: false,
        reason: 'stale',
        currentRev: Number(err.body['currentRev'] ?? revOf(fresh)),
        remote: await readNote(fresh).catch(() => ''),
      };
    }
    if (err.code === 'locked') {
      const expiresAt = err.body['expiresAt'];
      return { ok: false, reason: 'locked', expiresAt: typeof expiresAt === 'string' ? expiresAt : null };
    }
    throw err;
  }

  // Le corps part **tel qu'il est tapé**, en UTF-8 explicite : un `Blob` sans
  // type laisserait le navigateur en choisir un, et l'en-tête `content-type` est
  // signé — le moindre écart rend `SignatureDoesNotMatch`.
  const body = new Blob([markdown], { type: lease.upload.headers['content-type'] ?? 'text/markdown' });
  const put = await fetch(lease.upload.url, {
    method: 'PUT',
    headers: lease.upload.headers,
    body,
  });
  if (!put.ok) throw new Error(`envoi refusé par S3 (${put.status}) : ${await put.text()}`);

  // **Le numéro de version ne se lit qu'ici.** Le CORS du bucket l'expose
  // explicitement (`exposedHeaders`) ; sans cette ligne d'infrastructure, un
  // `PUT` réussi rendrait `null` et aucune note ne serait jamais enregistrable.
  const versionId = put.headers.get('x-amz-version-id');
  if (!versionId) {
    throw new Error(
      'S3 n’a pas rendu x-amz-version-id : le bucket doit l’exposer dans son CORS',
    );
  }

  try {
    const done = await completeContent(fid, lease.token, versionId);
    return {
      ok: true,
      rev: done.rev,
      size: done.size,
      updatedAt: done.updatedAt,
      excerpt: done.excerpt,
    };
  } catch (err) {
    if (!(err instanceof ApiError)) throw err;
    if (err.code === 'lock_lost') {
      return { ok: false, reason: 'lockLost', currentRev: Number(err.body['currentRev'] ?? 0) };
    }
    if (err.code === 'missing_bytes') return { ok: false, reason: 'missingBytes' };
    throw err;
  }
}

/**
 * Restaure une révision : **relire ses octets et les réenregistrer**.
 *
 * Il n'existe pas de route de restauration, et c'est délibéré côté serveur — une
 * restauration qui ferait reculer `ContentRev` casserait le verrou de tous les
 * appareils qui ont déjà la révision suivante. Restaurer est donc *écrire à
 * nouveau* : l'historique gagne une révision au lieu d'en perdre, et le retour en
 * arrière est lui-même annulable.
 */
export async function restoreRevision(entity: Entity, rev: number): Promise<SaveOutcome> {
  const text = await readRevision(entity.Fid ?? '', rev);
  return saveNote(entity, text);
}
